import axios from 'axios'

// Convert applicationServerKey to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
}

/**
 * Subscribe to push notifications
 * @returns {Promise<success>}
 */
export const subscribeUser = (user) => {
  if (!('serviceWorker' in navigator && 'Notification' in window && 'localStorage' in window)) {
    console.warn('Incompatible browser for push notification')
    return Promise.resolve(false)
  }

  // Remember the VAPID key so we can hand it to the SW if it needs to resubscribe
  if (user && user.vapid_public_key) setVapidPublicKey(user.vapid_public_key)

  // Check notification permission first
  if (Notification.permission === 'denied') {
    console.warn('Notification permission denied by user')
    return Promise.resolve(false)
  }

  // Request permission if not granted
  if (Notification.permission === 'default') {
    return Notification.requestPermission()
      .then(permission => {
        console.log('Notification permission:', permission)
        if (permission !== 'granted') {
          return false
        }
        return subscribeUser(user) // Retry after permission granted
      })
  }

  return navigator.serviceWorker.ready
    .then(registration => {
      if (!registration.pushManager) {
        // Maybe iOS on iPhone or iPad - should ask for adding to Home Screen
        console.log('PushManager not available, maybe should install app on iOS...')
        return
      }
      console.log('Service Worker ready, attempting push subscription...')
      // Use the PushManager to get the user's subscription to the push service
      return registration.pushManager.getSubscription()
        .then(subscription => {
          const applicationServerKey = urlBase64ToUint8Array(user.vapid_public_key)

          // If a subscription was found whose key matches, return it
          if (subscription && subscription.options.applicationServerKey) {
            const currentKey = new Uint8Array(subscription.options.applicationServerKey)
            if (applicationServerKey.length === currentKey.length && currentKey.every((val, index) => val === applicationServerKey[index])) {
              console.log('Existing push subscription found')
              return subscription
            }
            console.log('Existing push subscription found but application key changed, unsubscribe old subscription...')
            return subscription.unsubscribe()
              .then(() => axios.delete('/api/pushsubs', { data: subscription }))
              .then(() => {
                localStorage.removeItem('service:push:' + user.id)
                // Create a fresh subscription with the new key (do NOT reuse the dead one)
                return registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey,
                })
              })
          }
          // No usable subscription: create a new one
          console.log('Creating new push subscription...  vapid_key=' + user.vapid_public_key)
          return registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          })
        })
    })
    .then(subscription => {
      // Send the subscription details to the server
      console.log('Sending subscription to server...')
      return axios.post('/api/pushsubs', subscription)
        .then(() => {
          console.log('Push subscription successful')
          localStorage.setItem('service:push:' + user.id, JSON.stringify(subscription))
          return true
        })
        .catch(err => {
          console.error('Failed to save subscription to server:', err.response?.data || err.message)
          throw err
        })
    })
    .catch(err => {
      if (Notification.permission === 'denied') {
        console.warn('Permission for notifications was denied')
      } else {
        console.error('Unable to subscribe to push:', err)
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack,
        })
      }
      localStorage.removeItem('service:push:' + user.id)
      return false
    })
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<success>}
 */
export const unsubscribeUser = (user) => {
  // Accept either a user object, a user id, or nothing (then clear all known keys)
  let userId = null
  if (user && typeof user === 'object') userId = user.id
  else if (typeof user === 'number' || typeof user === 'string') userId = user

  const key = userId ? 'service:push:' + userId : null
  const stored = key ? localStorage.getItem(key) : null

  // Also unsubscribe from the browser's pushManager so the device stops receiving
  let browserUnsub = Promise.resolve(true)
  if ('serviceWorker' in navigator) {
    browserUnsub = navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub => (sub ? sub.unsubscribe() : true))
      .catch(() => false)
  }

  // Remove from server if we have a stored subscription
  let serverUnsub = Promise.resolve(true)
  if (stored) {
    serverUnsub = axios.delete('/api/pushsubs', { data: JSON.parse(stored) })
      .catch(err => { console.error(err); return false })
  }

  return Promise.all([browserUnsub, serverUnsub])
    .then(() => {
      if (key) localStorage.removeItem(key)
      return true
    })
}

// Promise when serviceWorker is initialized
let serviceWorkerPromise = Promise.resolve(false)

// Last known VAPID public key (set by subscribeUser / isPushEnabled callers)
let lastVapidPublicKey = null

export const setVapidPublicKey = (key) => {
  lastVapidPublicKey = key
}

/**
 * Start service worker
 */
export default (vue) => {
  if (!('serviceWorker' in navigator && 'Notification' in window && 'localStorage' in window)) {
    console.warn('Service Worker, Notifications, or localStorage not supported')
    return
  }
  serviceWorkerPromise = new Promise((resolve) => {
    window.addEventListener('load', () => {
      console.log('Registering Service Worker...')
      // Install and register service worker (bypass HTTP cache so updates land)
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
        .then(registration => {
          console.log('ServiceWorker registration successful with scope: ', registration.scope)

          // Detect a new service worker taking over and force it active immediately
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing
            if (!newWorker) return
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('New ServiceWorker installed; activating...')
                newWorker.postMessage({ skipWaiting: true })
              }
            })
          })

          // When the new SW takes control, reload once so clients use the new SW
          let reloading = false
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (reloading) return
            reloading = true
            console.log('ServiceWorker controller changed; reloading to use new version')
            window.location.reload()
          })

          // Wait for the service worker to be ready (installed + activated)
          return navigator.serviceWorker.ready
        })
        .then(registration => {
          console.log('ServiceWorker is ready and active')
          resolve(true)
        })
        .catch(err => {
          console.error('ServiceWorker registration or activation failed: ', err)
          resolve(false)
        })

      // Handler for messages coming from the service worker
      navigator.serviceWorker.addEventListener('message',
        ev => {
          const data = ev.data
          // SW asks for the VAPID public key (used by pushsubscriptionchange)
          if (data && data.requestVapidKey) {
            if (ev.source && lastVapidPublicKey) {
              ev.source.postMessage({ vapid_public_key: lastVapidPublicKey })
            }
            return
          }
          // SW asks to skip waiting (apply update immediately)
          if (data && data.skipWaiting) {
            return
          }
          if (data.redirect && (data.redirect.startsWith('http://') || data.redirect.startsWith('https://'))) {
            if (data.redirect.startsWith(window.origin)) {
              vue.$router.push(data.redirect.substring(window.origin.length))
            } else if (data.redirect.startsWith('http://localhost')) {
              vue.$router.push(data.redirect.substring(data.redirect.indexOf('/', 10)))
            } else {
              vue.$router.redirect(data.redirect)
            }
          }
        })
    })
  })
}

/**
 * Check that notification push is available
 * @returns {Promise<bool>}
 */
export const isPushAvailable = () => {
  return serviceWorkerPromise
}

/**
 * Get the live PushSubscription from the browser, or null.
 * @returns {Promise<PushSubscription|null>}
 */
export const getLiveSubscription = () => {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null)
  return navigator.serviceWorker.ready
    .then(reg => reg.pushManager.getSubscription())
    .catch(() => null)
}

/**
 * Check if notification push are enabled.
 * Verifies a live PushSubscription exists rather than trusting localStorage,
 * and re-syncs the server record if the browser has a subscription that the
 * client lost track of (e.g. after SW reinstall / browser rotation).
 * @returns {Promise<bool>}
 */
export const isPushEnabled = (user) => {
  if (!user) return Promise.resolve(false)
  if (user.vapid_public_key) setVapidPublicKey(user.vapid_public_key)
  return isPushAvailable()
    .then(available => {
      if (!available) return false
      return getLiveSubscription().then(subscription => {
        if (!subscription) {
          // No live subscription: clear stale flag and report disabled
          localStorage.removeItem('service:push:' + user.id)
          return false
        }
        // Live subscription present: make sure the server knows about it
        return syncSubscription(user, subscription).then(() => true)
      })
    })
}

/**
 * Ensure the server has this subscription recorded and localStorage is in sync.
 * @returns {Promise<bool>}
 */
export const syncSubscription = (user, subscription) => {
  if (!user || !subscription) return Promise.resolve(false)
  const stored = localStorage.getItem('service:push:' + user.id)
  const subJson = JSON.stringify(subscription)
  if (stored === subJson) return Promise.resolve(true)
  console.log('Re-syncing push subscription with server...')
  return axios.post('/api/pushsubs', subscription)
    .then(() => {
      localStorage.setItem('service:push:' + user.id, subJson)
      return true
    })
    .catch(err => {
      console.error('Failed to re-sync subscription:', err.response?.data || err.message)
      return false
    })
}

/**
 * Return the subscription json data, if notification are enabled
 * @returns {string}
 */
export const getSubJson = (user_id) => {
  return localStorage.getItem('service:push:' + user_id)
}
